import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import type { Block } from '@yuan-math/curriculum';
import { Figure, Math, MathBlock } from '@yuan-math/ui';

/**
 * The lesson renderer.
 *
 * Content is data (§24); this is the only place that decides how a block looks.
 * Adding a new block type means adding a case here and a variant to the type —
 * never editing a lesson file, and never embedding JSX in content.
 */
export function LessonBlock({ block }: { block: Block }): JSX.Element | null {
  switch (block.type) {
    case 'text':
      return <p className="prose">{renderInline(block.value)}</p>;

    case 'math':
      return <MathBlock latex={block.latex} {...(block.caption ? { caption: block.caption } : {})} />;

    case 'steps':
      return (
        <ol className="steps">
          {block.steps.map((step, index) => (
            <li key={index} className="step">
              <div className="step__label">{renderInline(step.label)}</div>
              <div className="step__math">
                <Math latex={step.latex} display />
              </div>
              {step.note ? <p className="step__note">{renderInline(step.note)}</p> : null}
            </li>
          ))}
        </ol>
      );

    case 'callout':
      return (
        <aside className={`callout callout--${block.tone}`}>
          {block.title ? <p className="callout__title">{block.title}</p> : null}
          <p className="callout__body">{renderInline(block.value)}</p>
        </aside>
      );

    case 'list':
      return block.ordered ? (
        <ol className="prose-list">
          {block.items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </ol>
      ) : (
        <ul className="prose-list">
          {block.items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </ul>
      );

    case 'figure':
      return (
        <figure className="lesson-figure">
          <Figure figure={block.figure} />
          {block.caption ? <figcaption>{renderInline(block.caption)}</figcaption> : null}
        </figure>
      );

    case 'table':
      return (
        <div className="table-wrap">
          <table className="lesson-table">
            <thead>
              <tr>
                {block.headers.map((header) => (
                  <th key={header} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'practice':
      return (
        <div className="practice-block">
          <div>
            <p className="practice-block__prompt">{block.prompt ?? 'Practise this now.'}</p>
            <p className="practice-block__meta">{block.count} problems, chosen for where you are.</p>
          </div>
          <Link to={`/practice/${block.conceptId}`} className="btn btn--primary btn--sm">
            Start practice
          </Link>
        </div>
      );

    default:
      return null;
  }
}

/**
 * Inline formatting for lesson prose.
 *
 * Content uses `**bold**` and `*italic*`; anything richer belongs in a block
 * type rather than in a markup dialect nobody documented. Splitting on the
 * markers keeps this a pure function with no `dangerouslySetInnerHTML`.
 */
function renderInline(value: string): JSX.Element[] {
  const parts = value.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    return <span key={index}>{part}</span>;
  });
}
